import { useEffect, useMemo, useState } from "react";
import { storyblokEditable, useStoryblokState } from "@storyblok/react";

type StoryblokAdapters = typeof import("../lib/adapters/storyblok/page-adapter");

// The Storyblok adapter graph is only executed here inside the visual-editor
// live preview (sbPreviewAllowed), but it is heavy — measured 2026-08-03: the
// adapter modules plus transitive fixtures (e.g. flip-cards.cms) made up the
// bulk (~200KB) of the ~240KB pages/index client chunk. A static import would
// pull all of it into every visitor's page bundle, so it is loaded on demand
// when preview mode is active (consumers: pages/index for the Pages Router,
// sb-preview-canvas for the App Router).

// Bounded immediate retries: enough to absorb a transient race without a
// backoff timer. A persistent failure (e.g. stale-deploy chunk 404) exhausts
// quickly with one log per attempt, and a page reload fully recovers because
// the state is per-mount — proportionate for an editor-only path.
export const ADAPTER_LOAD_MAX_ATTEMPTS = 4;

export function useStoryblokLivePage({
  sbPreviewAllowed,
  pageAdapterProps,
  initialComponents,
  sbRelationResolveKey
}: {
    sbPreviewAllowed: boolean;
    pageAdapterProps: any;
    initialComponents: any[];
    sbRelationResolveKey?: string;
}) {
  // Always call hooks in the same order
  const [adapters, setAdapters] = useState<StoryblokAdapters | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const baseCacheEntry = pageAdapterProps?.pagesCache?.[0] || {};
  const baseStory = baseCacheEntry?.value || baseCacheEntry;
  const baseCacheKey = baseCacheEntry?.cacheKey || pageAdapterProps?.pagesInfo?.[0]?.cacheKey;
  const sbStory = useStoryblokState(baseStory, {
    resolveRelations: sbRelationResolveKey
  });

  useEffect(() => {
    // pageAdapterProps gate: on preview-enabled tiers a page with nothing to
    // adapt must not fetch the adapter chunk at all.
    if (!sbPreviewAllowed || !pageAdapterProps || adapters || loadAttempt >= ADAPTER_LOAD_MAX_ATTEMPTS) {
      return undefined;
    }
    let cancelled = false;
    // Deep path (not the barrel): the barrel would drag dynamic-page-
    // components-adapter and its personalization subtree into this chunk too.
    // No module-scope cache: webpack already caches the loaded chunk, so a
    // remount re-adapts after one microtask; per-mount state keeps SSR and
    // the first client render deterministic (both return the server output).
    import("../lib/adapters/storyblok/page-adapter")
      .then(mod => {
        if (!cancelled) {
          setAdapters(mod);
        }
      })
      .catch(err => {
        if (cancelled) {
          return;
        }
        // Editor preview stays on server-adapted components. Bumping the
        // attempt counter re-runs this effect for a bounded retry; after the
        // cap the editor keeps server output and the errors give the trace
        // (a silent catch left it permanently stale with no signal at all).
        console.error("[useStoryblokLivePage] adapter chunk load failed", err);
        setLoadAttempt(attempt => attempt + 1);
      });
    return () => {
      cancelled = true;
    };
    // Boolean(): the effect only cares whether props exist — re-running on
    // every new props identity would cancel in-flight loads without consuming
    // a retry attempt, bypassing the cap.
  }, [sbPreviewAllowed, Boolean(pageAdapterProps), adapters, loadAttempt]);

  // Gate logic after hooks
  const components = useMemo(() => {
    if (!sbPreviewAllowed) {
      return initialComponents;
    }
    if (!sbStory || !pageAdapterProps) {
      return initialComponents;
    }
    if (!adapters) {
      // Adapter chunk still loading (editor preview only): keep server output.
      return initialComponents;
    }
    try {
      const pagesCache = adapters.pageAdapter({
        ...pageAdapterProps,
        pagesCache: [{
          value: sbStory,
          cacheKey: baseCacheKey
        }]
      })
      const cacheKey = baseCacheKey;
      const liveEntry = cacheKey ? pagesCache?.[cacheKey] : null;
      if (!liveEntry || !Array.isArray(liveEntry.components)) {
        return initialComponents;
      }
      return liveEntry.components.map(component => {
        if (!component?.type) {
          return component;
        }
        const blok = component.props || {};
        let editableProps = {};
        let blokForEditable = blok;
        if (blok.__ref_editable){
          blokForEditable = {
            ...(blok.reference?.content || blok),
            _uid: blok.__ref_uid,
            _editable: blok.__ref_editable
          }
        }
        if (blokForEditable?._editable) {
          editableProps = storyblokEditable(blokForEditable);
        }
        return {
          ...component,
          editableProps
        };
      });
    } catch (err) {
      // Same contract as a failed chunk load: keep server output, keep a
      // trace. Logging from the memo (render phase) is deliberate — this is
      // an editor-only diagnostic and a duplicated line under a StrictMode
      // replay is acceptable; state machinery just to relocate a log is not.
      console.error("[useStoryblokLivePage] live adaptation failed", err);
      return initialComponents;
    }
  }, [
    sbPreviewAllowed,
    (sbStory as any)?.cv,
    baseCacheKey,
    pageAdapterProps,
    initialComponents,
    adapters
  ]);
  return {
    components,
    sbStory: sbPreviewAllowed ? sbStory : null
  };
}

export default useStoryblokLivePage;
